import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { DEVICE_WIDTH, previewColors, SYSTEM_FONT_STACK } from './previewTheme'

const t = buildTranslate('live_updates.preview')

const CLOCK_FONT_SIZE = '64px'
const CARDS_GAP = '12px'

export interface IosLockScreenProps {
  children: React.ReactNode
}

// iOS lock-screen frame (salvaged from PR #3747): dark-teal wallpaper, a
// decorative status bar, the date + big clock, then the hosted cards (push
// banner and/or Live Activity). All off-theme literals are annotated in
// previewTheme — this is OS chrome, not Thanx chrome.
const IosLockScreen = ({ children }: IosLockScreenProps) => {
  const [css] = useStyletron()

  return (
    <div
      role="group"
      aria-label={t('ios_frame_label')}
      className={css({
        width: DEVICE_WIDTH,
        maxWidth: '100%',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        border: `8px solid ${previewColors.deviceBezel}`,
        borderBottom: 'none',
        backgroundColor: previewColors.iosWallpaper,
        fontFamily: SYSTEM_FONT_STACK,
        paddingTop: '12px',
        paddingBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {/* iOS status bar. Glyphs are decorative CSS shapes. */}
      <div
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '6px',
          padding: '0 20px',
          height: '20px',
        })}
      >
        {/* Cellular signal bars */}
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
          })}
        >
          {['4px', '6px', '8px', '10px'].map(height => (
            <div
              key={height}
              className={css({
                width: '3px',
                height,
                borderRadius: '1px',
                backgroundColor: palette.white,
              })}
            />
          ))}
        </div>
        {/* Wi-Fi (quarter-circle wedge) */}
        <div
          className={css({
            width: '14px',
            height: '10px',
            borderTopLeftRadius: '14px',
            borderTopRightRadius: '14px',
            backgroundColor: palette.white,
          })}
        />
        {/* Battery */}
        <div
          className={css({
            width: '22px',
            height: '11px',
            borderRadius: '3px',
            border: `1px solid ${palette.white}`,
            padding: '1px',
          })}
        >
          <div
            className={css({
              width: '100%',
              height: '100%',
              borderRadius: '1px',
              backgroundColor: palette.white,
            })}
          />
        </div>
      </div>

      {/* Date + big clock */}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '24px',
          marginBottom: '28px',
        })}
      >
        <Text.BodyText3
          color="white"
          className={css({ fontFamily: SYSTEM_FONT_STACK })}
        >
          {t('status_date')}
        </Text.BodyText3>
        <div
          className={css({
            color: palette.white,
            fontFamily: SYSTEM_FONT_STACK,
            fontSize: CLOCK_FONT_SIZE,
            fontWeight: 600,
            lineHeight: '1.1',
          })}
        >
          {t('ios_time')}
        </div>
      </div>

      <div
        className={css({
          padding: '0 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: CARDS_GAP,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default IosLockScreen
